import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRightIcon } from '@heroicons/react/20/solid';
import { useAuth } from '../../../contexts/AuthContext';
import { bookingApi } from '../../../utils/bookingApi';
import { Sheet, useConfirm, useToast } from '../../../components/ui';
import { statusFor, formatDate, slotLabel, money, DAY_SHORT } from './membershipHelpers';

const COMMITMENT_TONE = { ACTIVE: 'good', PAUSED: 'off', WAITLISTED: 'wait' };
const COMMITMENT_LABEL = { ACTIVE: 'Active', PAUSED: 'Paused', WAITLISTED: 'Waitlisted' };

// The member page this membership belongs to: the first parent, or the
// participant's own account for adults.
export function memberLinkFor(gymnast) {
  const guardian = gymnast.guardians?.[0];
  const userId = guardian?.id || gymnast.userId;
  if (!userId) return null;
  const name = guardian ? `${guardian.firstName} ${guardian.lastName}` : `${gymnast.firstName} ${gymnast.lastName}`;
  return { to: `/booking/admin/members/${userId}?child=${gymnast.id}`, name };
}

function Block({ title, children }) {
  return (
    <div className="ms-ed__block">
      <h3 className="ms-ed__title">{title}</h3>
      {children}
    </div>
  );
}

// A small pop-up for one change. save() returns true when it worked; a
// failure's message shows in the pop-up.
function EditSheet({ title, saveLabel, dirty, onSave, onClose, children }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const submit = async () => {
    setError(null);
    setSaving(true);
    const problem = await onSave();
    if (problem) { setError(problem); setSaving(false); }
  };
  return (
    <Sheet
      title={title}
      onClose={onClose}
      dirty={dirty}
      onSubmit={submit}
      footer={(requestClose) => (
        <div className="bk-actions">
          <button type="submit" className="bk-btn bk-btn--primary" disabled={saving}>{saving ? 'Saving…' : saveLabel}</button>
          <button type="button" className="bk-btn" onClick={requestClose}>Cancel</button>
        </div>
      )}
    >
      <div className="ms-form">
        {children}
        {error && <p className="bk-form-error" role="alert">{error}</p>}
      </div>
    </Sheet>
  );
}

// Everything about one membership, shown when its row is expanded.
// canManage: Manage access to payments; without it the membership is read-only.
export default function MembershipEditor({ membership: m, templates, onChanged, canManage = true }) {
  // The member's page is only linked for staff who can see members
  const { can } = useAuth();
  const confirm = useConfirm();
  const toast = useToast();
  const startAmount = (m.monthlyAmount / 100).toFixed(2);
  const [editing, setEditing] = useState(null); // 'amount' | 'start' | 'slot'
  const [amount, setAmount] = useState(startAmount);
  const [proration, setProration] = useState('create_prorations');
  const [startDate, setStartDate] = useState(m.startDate.slice(0, 10));
  const [newSlot, setNewSlot] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const status = statusFor(m);
  const name = `${m.gymnast.firstName} ${m.gymnast.lastName}`;
  const commitments = m.gymnast.commitments || [];
  const live = m.status !== 'CANCELLED';
  const billedNow = !m.paidOutside && m.stripeSubscriptionId && ['ACTIVE', 'PAUSED'].includes(m.status);
  const memberLink = can('members') ? memberLinkFor(m.gymnast) : null;

  const close = () => {
    setEditing(null);
    setAmount(startAmount);
    setStartDate(m.startDate.slice(0, 10));
    setNewSlot('');
  };

  // For buttons on the page: errors show above the actions
  const run = async (fn, success) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      if (success) toast(success);
      await onChanged();
      return true;
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong. Try again.');
      return false;
    } finally {
      setBusy(false);
    }
  };

  // For the pop-ups: returns an error message, or nothing when it worked
  const save = async (fn, success) => {
    try {
      await fn();
    } catch (err) {
      return err.response?.data?.error || 'Something went wrong. Try again.';
    }
    setEditing(null);
    toast(success);
    try { await onChanged(); } catch { setError("Saved, but couldn't refresh the list. Reload the page."); }
    return null;
  };

  const saveAmount = async () => {
    const pence = Math.round(parseFloat(amount) * 100);
    // Paid outside the app: for the club's reference only, so 0 is fine
    if (!Number.isFinite(pence) || pence < (m.paidOutside ? 0 : 1)) return 'Enter a valid amount.';
    const data = { monthlyAmount: pence };
    if (billedNow) data.prorationBehavior = proration;
    return save(() => bookingApi.updateMembership(m.id, data), 'Amount updated');
  };

  const saveStart = async () => {
    if (!startDate) return 'Choose a start date.';
    return save(() => bookingApi.updateMembership(m.id, { startDate }), 'Start date updated');
  };

  const addSlot = async () => {
    if (!newSlot) return 'Choose a session.';
    const data = { gymnastId: m.gymnastId, templateId: newSlot };
    // A slot added before the membership starts begins with it
    if (m.status === 'SCHEDULED') data.startDate = m.startDate;
    const problem = await save(() => bookingApi.createCommitment(data), 'Slot added');
    if (!problem) setNewSlot('');
    return problem;
  };

  const removeSlot = async (c) => {
    const ok = await confirm({
      title: `Remove ${DAY_SHORT[c.template.dayOfWeek]} ${c.template.startTime}?`,
      message: `${name} stops being booked into ${DAY_SHORT[c.template.dayOfWeek]} ${slotLabel(c.template)} each week.`,
      confirmLabel: 'Remove slot',
    });
    if (ok) await run(() => bookingApi.deleteCommitment(c.id), 'Slot removed');
  };

  const cancelMembership = async () => {
    const ok = await confirm({
      title: `Cancel ${name}'s membership?`,
      message: m.paidOutside ? 'Their weekly slots are removed.' : 'Payments stop straight away and their weekly slots are removed.',
      confirmLabel: 'Cancel membership',
      cancelLabel: 'Keep it',
    });
    if (ok) await run(() => bookingApi.deleteMembership(m.id), 'Membership cancelled');
  };

  const takenTemplateIds = new Set(commitments.map(c => c.templateId));
  const slotOptions = templates
    .filter(t => t.isActive && !takenTemplateIds.has(t.id))
    .sort((a, b) => ((a.dayOfWeek + 6) % 7) - ((b.dayOfWeek + 6) % 7) || a.startTime.localeCompare(b.startTime));

  return (
    <div className="ms-ed">
      {error && <p className="bk-form-error" role="alert">{error}</p>}
      <Block title="Amount">
        <div className="ms-line">
          <span className="ms-big">{money(m.monthlyAmount)} <span className="ms-muted">a month</span></span>
          {live && canManage && <button type="button" className="bk-btn bk-btn--sm" onClick={() => setEditing('amount')}>Change</button>}
        </div>
        {m.paidOutside && (
          <span className="ms-muted ms-small">Paid outside the app. The club collects it; the app doesn't take or track payments.</span>
        )}
      </Block>

      <Block title="Start date">
        <div className="ms-line">
          <span>{formatDate(m.startDate)}</span>
          {m.status === 'SCHEDULED'
            ? canManage && <button type="button" className="bk-btn bk-btn--sm" onClick={() => setEditing('start')}>Change</button>
            : <span className="ms-muted ms-small">Fixed once started</span>}
        </div>
      </Block>

      <Block title="Weekly slots">
        {commitments.length === 0 && <span className="ms-muted ms-small">No weekly slots.</span>}
        {commitments.map(c => (
          <div key={c.id} className="ms-slot">
            <span><strong>{DAY_SHORT[c.template.dayOfWeek]}</strong> {slotLabel(c.template)}</span>
            <span className="ms-actions">
              <span className={`bk-pill bk-pill--${COMMITMENT_TONE[c.status] || 'off'}`}>{COMMITMENT_LABEL[c.status] || c.status}</span>
              {live && canManage && (
                <button type="button" className="bk-btn bk-btn--sm" disabled={busy} onClick={() => removeSlot(c)}>Remove</button>
              )}
            </span>
          </div>
        ))}
        {live && canManage && (
          <button type="button" className="bk-btn bk-btn--sm ms-self-start" onClick={() => setEditing('slot')}>Add slot</button>
        )}
      </Block>

      <Block title="Status">
        <div className="ms-line">
          <span className={`bk-pill bk-pill--${status.tone}`}>{status.label}</span>
          <span className="ms-actions">
            {canManage && status.key === 'ACTIVE' && (
              <button type="button" className="bk-btn bk-btn--sm" disabled={busy}
                onClick={() => run(() => bookingApi.updateMembership(m.id, { status: 'PAUSED' }), 'Paused, with their slots')}>
                Pause
              </button>
            )}
            {canManage && status.key === 'PAUSED' && (
              <button type="button" className="bk-btn bk-btn--sm bk-btn--primary" disabled={busy}
                onClick={() => run(() => bookingApi.updateMembership(m.id, { status: 'ACTIVE' }), 'Active again')}>
                Resume
              </button>
            )}
            {canManage && status.key === 'CANCELLING' && (
              <button type="button" className="bk-btn bk-btn--sm bk-btn--primary" disabled={busy}
                onClick={() => run(() => bookingApi.resumeMembership(m.id), 'Cancellation undone')}>
                Undo cancellation
              </button>
            )}
          </span>
        </div>
        {status.key === 'CANCELLING' && (
          <span className="ms-muted ms-small">
            Cancels on {formatDate(m.scheduledCancelAt)}{m.scheduledCancelReason === 'BG_EXPIRED' ? ' because their British Gymnastics membership expired' : ''}.
          </span>
        )}
        {['ACTIVE', 'PAUSED'].includes(status.key) && (
          <span className="ms-muted ms-small">Pausing also pauses their weekly slots.</span>
        )}
        {canManage && m.status === 'PENDING_PAYMENT' && !m.paidOutside && (
          <div className="bk-row-actions">
            <button type="button" className="bk-btn bk-btn--sm" disabled={busy}
              onClick={() => run(() => bookingApi.sendMembershipReminder(m.id), 'Reminder sent')}>
              Send reminder
            </button>
            <button type="button" className="bk-btn bk-btn--sm" disabled={busy}
              onClick={() => run(() => bookingApi.resetMembership(m.id), 'Payment setup reset')}>
              Reset payment setup
            </button>
          </div>
        )}
        {live && canManage && (
          <button type="button" className="bk-btn bk-btn--sm bk-btn--outline-danger ms-self-start" disabled={busy} onClick={cancelMembership}>
            Cancel membership
          </button>
        )}
      </Block>

      {memberLink && (
        <div className="ms-ed__block">
          <Link className="ms-member-link" to={memberLink.to}>
            View {memberLink.name}'s page <ChevronRightIcon className="ms-member-link__icon" aria-hidden="true" />
          </Link>
        </div>
      )}

      {editing === 'amount' && (
        <EditSheet title={`Change ${name}'s amount`} saveLabel="Save amount" dirty={amount !== startAmount}
          onSave={saveAmount} onClose={close}>
          <label className="bk-label">Monthly amount (£)
            <input type="number" step="0.01" min={m.paidOutside ? '0' : '0.01'} className="bk-input" inputMode="decimal"
              value={amount} onChange={e => setAmount(e.target.value)} required />
          </label>
          {billedNow && (
            <fieldset className="ms-slots">
              <legend className="ms-field">When does it change?</legend>
              <label className="ms-radio">
                <input type="radio" name={`when-${m.id}`} checked={proration === 'create_prorations'} onChange={() => setProration('create_prorations')} />
                Apply now, with a pro-rata adjustment
              </label>
              <label className="ms-radio">
                <input type="radio" name={`when-${m.id}`} checked={proration === 'none'} onChange={() => setProration('none')} />
                From next month
              </label>
            </fieldset>
          )}
        </EditSheet>
      )}

      {editing === 'start' && (
        <EditSheet title={`Change ${name}'s start date`} saveLabel="Save date" dirty={startDate !== m.startDate.slice(0, 10)}
          onSave={saveStart} onClose={close}>
          <label className="bk-label">Start date
            <input type="date" className="bk-input" value={startDate} onChange={e => setStartDate(e.target.value)} required />
          </label>
          <span className="ms-muted ms-small">Their weekly slots move to the new date too.</span>
        </EditSheet>
      )}

      {editing === 'slot' && (
        <EditSheet title={`Add a slot for ${name}`} saveLabel="Add slot" dirty={!!newSlot} onSave={addSlot} onClose={close}>
          <label className="bk-label">Session to add
            <select className="bk-input" value={newSlot} onChange={e => setNewSlot(e.target.value)} required>
              <option value="">Choose a session</option>
              {slotOptions.map(t => (
                <option key={t.id} value={t.id}>{DAY_SHORT[t.dayOfWeek]} {slotLabel(t)}</option>
              ))}
            </select>
          </label>
        </EditSheet>
      )}
    </div>
  );
}
